import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaGraduationCap, 
  FaCheck, 
  FaTimes, 
  FaSearch,
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaTag
} from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminMentorshipRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await apiService.getMentorshipRequests();
      // Parse preferred_topics from JSON string to array if needed
      const requestsData = (response.data || []).map(request => {
        // Parse preferred_topics
        if (typeof request.preferred_topics === 'string') {
          try {
            request.preferred_topics = JSON.parse(request.preferred_topics) || [];
          } catch {
            request.preferred_topics = [];
          }
        }
        if (!Array.isArray(request.preferred_topics)) {
          request.preferred_topics = [];
        }
        return request;
      });
      setRequests(requestsData);
    } catch (error) {
      console.error('Error fetching mentorship requests:', error);
      toast.error('Failed to fetch mentorship requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await apiService.updateMentorshipStatus(id, status);
      toast.success(`Request ${status} successfully`);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update request status');
    }
  };

  // Helper function to safely display topics
  const displayTopics = (topics) => {
    if (!topics) return '';
    if (Array.isArray(topics)) {
      return topics.join(', ');
    }
    if (typeof topics === 'string') {
      try {
        const parsed = JSON.parse(topics);
        if (Array.isArray(parsed)) {
          return parsed.join(', ');
        }
        return topics;
      } catch {
        return topics;
      }
    }
    return '';
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = 
      request.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.goals?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    approved: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400',
    scheduled: 'bg-blue-500/20 text-blue-400',
    completed: 'bg-gray-500/20 text-gray-400',
    archived: 'bg-gray-500/20 text-gray-400'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Mentorship Requests</h1>
          <p className="text-gray-400 mt-1">Manage mentorship applications</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search requests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.map((request) => (
          <motion.div
            key={request.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center text-primary-400">
                    <FaGraduationCap />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{request.full_name}</h3>
                    <p className="text-gray-400 text-sm">{request.email}</p>
                  </div>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[request.status] || statusColors.pending}`}>
                    {request.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <FaUser className="text-xs" />
                    {request.experience_level || 'Not specified'}
                  </span>
                  {/* FIXED: Safely display preferred_topics */}
                  {request.preferred_topics && request.preferred_topics.length > 0 && (
                    <span className="flex items-center gap-1.5">
                      <FaTag className="text-xs" />
                      {displayTopics(request.preferred_topics)}
                    </span>
                  )}
                </div>

                {request.goals && (
                  <p className="text-gray-300 text-sm mt-2 line-clamp-2">
                    <span className="font-medium">Goals:</span> {request.goals}
                  </p>
                )}

                {request.availability && (
                  <p className="text-gray-400 text-sm mt-1 flex items-center gap-1.5">
                    <FaCalendarAlt className="text-xs" />
                    {request.availability}
                  </p>
                )}

                <p className="text-xs text-gray-500 mt-2">
                  Submitted: {new Date(request.created_at).toLocaleString()}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {request.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(request.id, 'approved')}
                      className="px-3 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-all duration-300 text-green-400 text-sm flex items-center gap-1.5"
                    >
                      <FaCheck size={12} />
                      Approve
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(request.id, 'rejected')}
                      className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-all duration-300 text-red-400 text-sm flex items-center gap-1.5"
                    >
                      <FaTimes size={12} />
                      Reject
                    </button>
                  </>
                )}
                {request.status === 'approved' && (
                  <button
                    onClick={() => handleStatusUpdate(request.id, 'scheduled')}
                    className="px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 transition-all duration-300 text-blue-400 text-sm"
                  >
                    Mark as Scheduled
                  </button>
                )}
                {request.status === 'scheduled' && (
                  <button
                    onClick={() => handleStatusUpdate(request.id, 'completed')}
                    className="px-3 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-all duration-300 text-green-400 text-sm"
                  >
                    Mark as Completed
                  </button>
                )}
                {request.status !== 'archived' && request.status !== 'rejected' && (
                  <button
                    onClick={() => handleStatusUpdate(request.id, 'archived')}
                    className="px-3 py-1.5 rounded-lg bg-gray-500/20 hover:bg-gray-500/30 transition-all duration-300 text-gray-400 text-sm"
                  >
                    Archive
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredRequests.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">No mentorship requests found.</p>
        </div>
      )}
    </div>
  );
};

export default AdminMentorshipRequests;